"use client";

import { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type * as z from "zod";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { StepBasicInfo } from "./step-basic-info";
import { StepIncomeExpense } from "./step-income-expense";
import { StepAssetsLiabilities } from "./step-assets-liabilities";
import { StepLifeGoals } from "./step-life-goals";
import { StepSimulationSettings } from "./step-simulation-settings";
import { StepConfirmation } from "./step-confirmation";
import { ChartPreview } from "./chart-preview";
import { SummaryPanel } from "./summary-panel";
import { Stepper } from "./stepper";
import { lifePlanSchema } from "@/lib/validation";
import { saveToLocalStorage, loadFromLocalStorage } from "@/lib/storage";
import { useI18n } from "@/lib/i18n/hook";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

export function LifePlanForm() {
  const router = useRouter();
  const { t } = useI18n();
  const [currentStep, setCurrentStep] = useState(1);
  const [autoSaveId, setAutoSaveId] = useState<NodeJS.Timeout | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [simulationData, setSimulationData] = useState<z.infer<
    typeof lifePlanSchema
  > | null>(null);

  const methods = useForm<z.infer<typeof lifePlanSchema>>({
    resolver: zodResolver(lifePlanSchema),
    defaultValues: {
      basicInfo: {
        name: "",
        age: 30,
        gender: "male",
        familyStructure: {
          hasSpouse: false,
          spouseAge: 0,
          children: [],
        },
      },
      income: {
        annualIncome: 0,
        salaryIncreaseRate: 3,
        annualBonus: 0,
      },
      expense: {
        housingCost: 0,
        utilities: 0,
        food: 0,
        communication: 0,
        transportation: 0,
        entertainment: 0,
        other: 0,
      },
      investment: {
        stocks: 0,
        bonds: 0,
        mutualFunds: 0,
        insurance: 0,
        other: 0,
      },
      assets: {
        savings: {
          amount: 0,
          return: 0,
          volatility: 0,
        },
        stocks: {
          amount: 0,
          return: 5,
          volatility: 15,
        },
        bonds: {
          amount: 0,
          return: 2,
          volatility: 10,
        },
        mutualFunds: {
          amount: 0,
          return: 5,
          volatility: 15,
        },
        insurance: {
          amount: 0,
          return: 0,
          volatility: 0,
        },
        other: {
          amount: 0,
          return: 0,
          volatility: 0,
        },
      },
      liabilities: {
        housingLoan: [
          {
            name: t("lifePlan.assetsLiabilities.liabilities.housingLoan"),
            amount: 0,
            period: 0,
            interestRate: 0,
          },
        ],
        otherLoans: [],
      },
      lifeGoals: {
        housingPurchase: {
          planned: false,
          year: 0,
          amount: 0,
        },
        educationFunds: [],
        otherGoals: [],
        retirementAge: 65,
        retirementMonthlyExpense: 0,
      },
      simulationSettings: {
        inflationRate: 1,
        investmentReturn: 3,
        simulationPeriod: 40,
      },
    },
  });

  // フォームデータの変更を監視して自動保存
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const subscription = methods.watch((value) => {
      if (timeoutId) clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        saveToLocalStorage("lifePlanData", value);
      }, 1000);
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [methods]);

  // 保存されたデータの読み込み
  useEffect(() => {
    const savedData = loadFromLocalStorage("lifePlanData");
    if (savedData) {
      methods.reset(savedData);
    }
  }, [methods]);

  const nextStep = () => {
    methods.trigger().then((isValid) => {
      if (isValid) {
        setCurrentStep((prev) => Math.min(prev + 1, steps.length));
      } else {
        const errors = methods.formState.errors;
        console.log(errors);
        const errorMessages = Object.entries(errors)
          .map(([field, error]) => `${field}: ${error.message}`)
          .join("\n");
        alert(t("lifePlan.validation.error", { errors: errorMessages }));
      }
    });
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSimulationStart = async (
    data: z.infer<typeof lifePlanSchema>
  ) => {
    setSimulationData(data);
    setIsConfirmDialogOpen(true);
  };

  const handleConfirmSimulation = () => {
    if (simulationData) {
      saveToLocalStorage("lifePlanData", simulationData);
      router.push("/life-plan/result");
    }
    setIsConfirmDialogOpen(false);
  };

  const handleCancelSimulation = () => {
    setIsConfirmDialogOpen(false);
    setSimulationData(null);
  };

  const steps = [
    { id: 1, title: t("lifePlan.steps.basicInfo") },
    { id: 2, title: t("lifePlan.steps.simulationSettings") },
    { id: 3, title: t("lifePlan.steps.incomeExpense") },
    { id: 4, title: t("lifePlan.steps.assetsLiabilities") },
    { id: 5, title: t("lifePlan.steps.lifeGoals") },
    { id: 6, title: t("lifePlan.steps.confirmation") },
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <StepBasicInfo />;
      case 2:
        return <StepSimulationSettings />;
      case 3:
        return <StepIncomeExpense />;
      case 4:
        return <StepAssetsLiabilities />;
      case 5:
        return <StepLifeGoals />;
      case 6:
        return <StepConfirmation />;
      default:
        return null;
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(nextStep)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card className="p-6">
              <Stepper steps={steps} currentStep={currentStep} />

              <div className="mt-8">{renderStepContent()}</div>

              <div className="mt-8 flex justify-between">
                {currentStep > 1 && (
                  <Button type="button" variant="outline" onClick={prevStep}>
                    {t("lifePlan.navigation.prev")}
                  </Button>
                )}
                {currentStep < steps.length ? (
                  <Button type="submit" className="ml-auto">
                    {t("lifePlan.navigation.next")}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    className="ml-auto"
                    onClick={methods.handleSubmit(handleSimulationStart)}
                  >
                    {t("lifePlan.navigation.startSimulation")}
                  </Button>
                )}
              </div>
            </Card>
          </div>

          <div className="space-y-6">
            <SummaryPanel />
            <ChartPreview />
          </div>
        </div>
      </form>
      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        onClose={handleCancelSimulation}
        onConfirm={handleConfirmSimulation}
        title={t("lifePlan.navigation.confirmStartTitle")}
        description={t("lifePlan.navigation.confirmStart")}
        confirmText={t("lifePlan.navigation.startSimulation")}
        cancelText={t("lifePlan.navigation.cancel")}
      />
    </FormProvider>
  );
}
